/**
 * Markdown <-> HTML conversion for the composer's Markdown mode.
 *
 * Why this exists: the RichTextEditor offers a raw-Markdown
 * composition mode next to the WYSIWYG surface (developer request,
 * 2026-06-11 - the team writes Markdown all day and wants to paste /
 * type it directly). The parent components' contract stays
 * ``onChange(html, text)``, so Markdown is converted to HTML at the
 * editor boundary and the rest of the app keeps thinking in HTML.
 *
 * Dialect parity: the backend renders API-supplied
 * ``body_markdown`` with markdown-it (CommonMark + GFM tables +
 * strikethrough). ``marked`` with ``gfm: true`` covers the same
 * constructs; minor edge-case divergence between the two parsers is
 * acceptable because each body is converted exactly once, by exactly
 * one of them.
 *
 * Safety: ``marked`` passes raw HTML in the source through untouched,
 * so the output goes through the same DOMPurify allowlist
 * (``sanitizeHtml``) every rendered body already passes. Quote
 * collapsing is explicitly disabled - that transform is for inbound
 * email rendering, never for authored content.
 */
import { Marked } from "marked";
import TurndownService from "turndown";
import { gfm } from "turndown-plugin-gfm";

import { sanitizeHtml } from "@/lib/sanitize";

const marked = new Marked({
  gfm: true,
  // Single newline stays a soft break (CommonMark semantics), same
  // as the backend renderer - a blank line separates paragraphs.
  breaks: false,
});

let turndown: TurndownService | null = null;

/** Lazy singleton - TurndownService builds its rule table at
 * construction and is only needed once the user first toggles an
 * HTML-seeded composer into Markdown mode. */
function getTurndown(): TurndownService {
  if (!turndown) {
    turndown = new TurndownService({
      headingStyle: "atx",
      codeBlockStyle: "fenced",
      bulletListMarker: "-",
      emDelimiter: "*",
    });
    turndown.use(gfm);
    // Turndown's stock listItem rule pads every marker to four
    // columns ("-   item", "1.  item"). Hand-written markdown is
    // "- item" / "1. item"; since this output lands in a textarea
    // for a human to keep editing, emit the conventional spacing.
    // Continuation lines indent by the prefix width so nested lists
    // stay valid CommonMark.
    turndown.addRule("tightListItem", {
      filter: "li",
      replacement: (content, node, options) => {
        const parent = node.parentNode as HTMLElement | null;
        let prefix = `${options.bulletListMarker} `;
        if (parent && parent.nodeName === "OL") {
          const start = parent.getAttribute("start");
          const index = Array.prototype.indexOf.call(parent.children, node);
          prefix = `${start ? Number(start) + index : index + 1}. `;
        }
        const body = content
          .replace(/^\n+/, "")
          .replace(/\n+$/, "\n")
          .replace(/\n/gm, `\n${" ".repeat(prefix.length)}`);
        const el = node as HTMLElement;
        return (
          prefix + body + (el.nextSibling && !/\n$/.test(body) ? "\n" : "")
        );
      },
    });
  }
  return turndown;
}

/**
 * Render Markdown to sanitized HTML, ready for the ``onChange(html,
 * text)`` contract / dangerouslySetInnerHTML.
 */
export function markdownToHtml(markdown: string): string {
  const raw = marked.parse(markdown, { async: false });
  return sanitizeHtml(raw, { collapseQuotes: false }).trim();
}

/**
 * Convert composer HTML to Markdown for editing in Markdown mode.
 *
 * Lossy by design where Markdown has no equivalent (underline,
 * colored spans, empty-paragraph spacer lines); the content text
 * always survives even when a wrapper tag does not.
 */
export function htmlToMarkdown(html: string): string {
  if (!html) return "";
  try {
    return getTurndown().turndown(html);
  } catch (err) {
    // Never lose the user's content over a conversion bug: fall back
    // to the raw HTML string, which Markdown mode shows verbatim and
    // marked will pass through (then sanitized) on the way back.
    console.error("htmlToMarkdown failed; editing raw HTML", err);
    return html;
  }
}
